import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { StrapiSurveyGridRow } from '../../../api/strapiCrm';
import {
  SignatureCapture,
  type SignatureCaptureHandle,
} from '../../../components/userSignature/SignatureCapture';

type Props = {
  open: boolean;
  mode: 'customer' | 'surveyor' | null;
  targetRows: StrapiSurveyGridRow[];
  selectedCount: number;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (signDataUrl: string) => void;
};

function signHeaderDetail(rows: StrapiSurveyGridRow[]): { left: string; right: string } {
  if (rows.length === 0) return { left: 'Tidak ada baris yang memenuhi syarat', right: '' };
  const bidangs = [
    ...new Set(rows.map((row) => row.bidang_pengujian_label).filter((label) => Boolean(label))),
  ];
  const points = rows.map((row) => row.point_label).filter((label) => Boolean(label));
  return {
    left: bidangs.join(', ') || '—',
    right: points.join(', '),
  };
}

export function SurveyBeritaAcaraSignDialog({
  open,
  mode,
  targetRows,
  selectedCount,
  submitting = false,
  onClose,
  onSubmit,
}: Props) {
  const padRef = useRef<SignatureCaptureHandle | null>(null);
  const [ready, setReady] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!open) setFullscreen(false);
  }, [open]);

  const handleSubmit = () => {
    const dataUrl = padRef.current?.exportPng();
    if (!dataUrl) return;
    onSubmit(dataUrl);
  };

  const saveDisabled = !ready || submitting || targetRows.length === 0;
  const titleText = mode === 'customer' ? 'Tanda tangan pelanggan' : 'Tanda tangan petugas';
  const headerDetail = signHeaderDetail(targetRows);

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (submitting) return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      fullScreen={fullscreen}
      data-testid="survey-berita-acara-sign-dialog"
      slotProps={{
        paper: {
          sx: fullscreen
            ? {
                height: '100dvh',
                maxHeight: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }
            : undefined,
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={
          fullscreen
            ? {
                flexShrink: 0,
                px: 1.5,
                pt: 'max(6px, env(safe-area-inset-top))',
                pb: 0.5,
              }
            : undefined
        }
      >
        {fullscreen ? (
          <Stack spacing={0.25}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
              <Typography variant="caption" noWrap sx={{ fontWeight: 600, minWidth: 0 }}>
                {titleText}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                {targetRows.length} baris
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ minWidth: 0, flex: 1 }}
              >
                {headerDetail.left}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ minWidth: 0, flex: 1, textAlign: 'right' }}
              >
                {headerDetail.right}
              </Typography>
            </Box>
          </Stack>
        ) : (
          titleText
        )}
      </DialogTitle>
      <DialogContent
        sx={
          fullscreen
            ? {
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 auto',
                minHeight: 0,
                overflow: 'hidden',
                px: 1,
                pt: 0.5,
                pb: 0.5,
              }
            : undefined
        }
      >
        {fullscreen ? null : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {targetRows.length} baris akan ditandatangani
              {selectedCount > targetRows.length
                ? ` (${selectedCount - targetRows.length} baris terpilih tidak memenuhi syarat)`
                : ''}
              .
            </Typography>
            {targetRows.length > 0 ? (
              <Stack spacing={0.25} sx={{ mb: 2 }}>
                {targetRows.map((row) => (
                  <Typography key={row.row_key} variant="caption" color="text.secondary">
                    {row.bidang_pengujian_label} — {row.point_label}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Tidak ada baris terpilih yang memenuhi syarat untuk tanda tangan ini.
              </Alert>
            )}
          </>
        )}
        <Box
          sx={
            fullscreen
              ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
              : undefined
          }
        >
          <SignatureCapture
            ref={padRef}
            open={open}
            disabled={submitting}
            allowDefault={mode === 'surveyor'}
            fillHeight={fullscreen}
            showHelperText={!fullscreen}
            showActions={!fullscreen}
            canvasTestId="survey-berita-acara-sign-canvas"
            onReadyChange={setReady}
            extraActions={
              fullscreen ? null : (
                <Button
                  size="small"
                  startIcon={<FullscreenIcon />}
                  onClick={() => setFullscreen(true)}
                  disabled={submitting}
                  data-testid="survey-berita-acara-sign-fullscreen"
                >
                  Layar penuh
                </Button>
              )
            }
          />
        </Box>
      </DialogContent>
      {fullscreen ? (
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 0.5,
            pt: 0.25,
            pb: 'max(4px, env(safe-area-inset-bottom))',
          }}
        >
          <Tooltip title="Hapus">
            <span>
              <IconButton
                aria-label="Hapus"
                onClick={() => padRef.current?.clear()}
                disabled={!ready || submitting}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Keluar layar penuh">
            <span>
              <IconButton
                aria-label="Keluar layar penuh"
                onClick={() => setFullscreen(false)}
                disabled={submitting}
                data-testid="survey-berita-acara-sign-fullscreen"
              >
                <FullscreenExitIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={submitting ? 'Menyimpan…' : 'Simpan'}>
            <span>
              <IconButton
                aria-label="Simpan"
                color="primary"
                onClick={handleSubmit}
                disabled={saveDisabled}
                data-testid="survey-berita-acara-sign-submit"
              >
                {submitting ? <CircularProgress size={22} /> : <SaveOutlinedIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Batal">
            <span>
              <IconButton aria-label="Batal" onClick={onClose} disabled={submitting}>
                <CloseIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ) : (
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Batal
          </Button>
          <Button
            variant="contained"
            disabled={saveDisabled}
            onClick={handleSubmit}
            data-testid="survey-berita-acara-sign-submit"
          >
            {submitting ? 'Menyimpan…' : 'Simpan'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
